import { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import { useGlobalAPI } from '../../../../hooks/useGlobalAPI';
import { useOcelContext } from '../../../../stores/OcelContext';
import { useGlobalFilters } from '../../../../stores/GlobalFilterContext/GlobalFilterContext';
import { VARIANTS_PER_PAGE } from '../../VariantExplorer/constants';
import { VARIANT_MODES, REPETITION_DISPLAY } from '../constants';
import variantSelectorExtendedApi from '../api/variantSelectorExtendedApi';

/**
 * Hook for managing extended variant selector state and API calls.
 * Extends the original useVariantSelector with activity sequence support.
 *
 * @param {string} selectedObjectType - Currently selected object type.
 * @param {string} activeFile - Currently active OCEL file identifier.
 * @param {string} variantMode - Variant grouping mode. Defaults to "exact" so
 *   existing callers retain the previous behaviour unchanged.
 * @param {string} repetitionDisplay - Loop multiplier rendering mode. Only
 *   meaningful when variantMode === LOOP_COMPRESSED. Defaults to "exact".
 * @param {string} [initialSortOption] - Sort option restored from saved config,
 *   applied once on mount via handleSortChange.
 * @param {Object|null} [appliedActivityFilter] - Snapshot of the Activity
 *   Filter last committed via Apply (or null after Reset), owned by the
 *   parent. Every automatic refetch triggered from inside this hook (variant
 *   mode / repetition display change, and the default of `refetchData()`)
 *   reuses this snapshot instead of dropping the filter — only the parent's
 *   Apply/Reset handlers ever change what it holds.
 */
export const useVariantExplorerExtended = (
  selectedObjectType,
  activeFile,
  variantMode = VARIANT_MODES.EXACT,
  repetitionDisplay = REPETITION_DISPLAY.EXACT,
  initialSortOption,
  appliedActivityFilter = null,
) => {
  const api = useGlobalAPI(activeFile);
  const { dispatch, filterState } = useGlobalFilters();
  const [variants, setVariants] = useState([]);
  const [summary, setSummary] = useState(null);
  const [activityCatalog, setActivityCatalog] = useState({});
  const [selectedVariants, setSelectedVariants] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortKey, setSortKey] = useState('trace_count');
  const [sortOrder, setSortOrder] = useState('desc');
  const [sortOption, setSortOption] = useState('Most common variant');
  const hasUserTouchedSelection = useRef(false);
  const [loading, setLoading] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [error, setError] = useState(null);
  const { ocelData, setOcelData, isFiltered, setIsFiltered } = useOcelContext();
  const [dateRangeOverride, setDateRangeOverride] = useState({ startDate: null, endDate: null, enabled: false });

  const appliedActivityFilterRef = useRef(appliedActivityFilter);
  useEffect(() => {
    appliedActivityFilterRef.current = appliedActivityFilter;
  });

  // Track if variant filtering is currently active
  const isVariantFilterActive = filterState?.variantFilter?.enabled && 
    filterState?.variantFilter?.variantActivitiesList?.length > 0;

  // Only the most recently started fetch may commit its result — a slower,
  // older request (e.g. superseded by a mode change) must not overwrite a newer
  // one, and a newer request must not be skipped just because one is in flight.
  const fetchSeqRef = useRef(0);

  // Reset all variant data when activeFile changes
  useEffect(() => {
    fetchSeqRef.current += 1; // drop any in-flight response for the previous file
    setVariants([]);
    setSummary(null);
    setActivityCatalog({});
    setSelectedVariants([]);
    setCurrentPage(1);
    setSortKey('trace_count');
    setSortOrder('desc');
    setLoading(false);
    setHasLoaded(false);
    setError(null);
    setOcelData(null);
    setIsFiltered(false);
    hasUserTouchedSelection.current = false;
  }, [activeFile]);

  useEffect(() => {
    if (selectedObjectType) {
      console.log('🔄 VariantExplorerExtended effect triggered:', {
        selectedObjectType,
        variantMode,
        repetitionDisplay,
      });
      hasUserTouchedSelection.current = false;
      setOcelData(null);
      setHasLoaded(false);
      // Reuse the last committed Activity Filter (if any) so switching modes
      // doesn't silently drop it — see appliedActivityFilterRef above.
      handleFetchVariants(null, appliedActivityFilterRef.current);
    }
    // repetitionDisplay only affects loop_segments output, so refetching when
    // it changes is what makes the Exact/Abstract toggle live-update.
  }, [selectedObjectType, variantMode, repetitionDisplay]);

  const handleFetchVariants = async (ids = null, activityFilter = null) => {
    const seq = ++fetchSeqRef.current;
    setLoading(true);
    setError(null);
    try {
      console.log('🔍 Fetching extended variants with current global filters (excluding variant filters)');

      // Get current global filters excluding variant filters, overriding the
      // date range with this tile's own local selection when set.
      const globalFilters = { ...filterState };
      delete globalFilters.variantFilter;
      if (dateRangeOverride?.enabled) {
        globalFilters.dateRange = dateRangeOverride;
      }

      // Build the filtered request using the global API
      const globalFilterRequest = api.buildFilteredRequest(globalFilters);

      const data = ids
        ? await variantSelectorExtendedApi.filterOCELByVariants(selectedObjectType, ids, activeFile)
        : await variantSelectorExtendedApi.getExtendedVariants(
            selectedObjectType,
            activeFile,
            globalFilterRequest,
            activityFilter,
            variantMode,
            repetitionDisplay,
          );
      if (seq !== fetchSeqRef.current) return;

      setVariants(data.variants || []);
      setSummary(data.summary || null);
      setActivityCatalog(data.activity_catalog || {});
      
      if (activityFilter && activityFilter.enabled) {
        const matchingVariantIds = (data.variants || []).map((v) => v.variant_id);
        setSelectedVariants(matchingVariantIds);
        console.log(`✅ Activity filter applied. Selected ${matchingVariantIds.length} matching variants from backend`);
      } else {
        setSelectedVariants((data.variants || []).map((v) => v.variant_id));
      }
      
      setCurrentPage(1);
      setHasLoaded(true);
      setOcelData(data);
      setIsFiltered(!!ids);
    } catch (err) {
      if (seq !== fetchSeqRef.current) return;
      console.error('Failed to fetch extended variant data:', err);
      setError('Failed to fetch extended variant data');
    } finally {
      if (seq === fetchSeqRef.current) setLoading(false);
    }
  };

  // Expose refetch function to parent component. Callers that are committing
  // a new filter state (Apply/Reset) always pass activityFilter explicitly;
  // automatic triggers (global filter change, local date-range change) call
  // this bare, so the default reuses the last committed snapshot instead of
  // dropping it — the same rule the mode-change effect above follows.
  const refetchData = useCallback((activityFilter = appliedActivityFilterRef.current) => {
    if (selectedObjectType) {
      console.log('🔄 Refetching extended variant data due to global filter change or activity filter');
      handleFetchVariants(null, activityFilter);
    }
  }, [selectedObjectType, activeFile, filterState, variantMode, repetitionDisplay, dateRangeOverride]);

  const handleToggleSelect = (variantId) => {
    hasUserTouchedSelection.current = true;
    setSelectedVariants((prev) =>
      prev.includes(variantId)
        ? prev.filter((id) => id !== variantId)
        : [...prev, variantId]
    );
  };

  const toggleSelectAll = () => {
    hasUserTouchedSelection.current = true;
    if (selectedVariants.length === variants.length) {
      setSelectedVariants([]);
    } else {
      setSelectedVariants(variants.map((v) => v.variant_id));
    }
  };

  const setSorting = (key, order = 'asc') => {
    setSortKey(key);
    setSortOrder(order);
  };

  const handleSortChange = (selected) => {
    setSortOption(selected);
    if (selected === 'Fastest') {
      setSorting('median_lead_time', 'asc');
    } else if (selected === 'Slowest') {
      setSorting('median_lead_time', 'desc');
    } else {
      setSorting('trace_count', 'desc');
    }
    setCurrentPage(1);
  };

  // Restore sort option from saved config (mount-only)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (initialSortOption) {
      handleSortChange(initialSortOption);
    }
  }, []);

  const handleColumnSort = (key) => {
    if (sortKey === key) {
      setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortOrder('asc');
    }
    setCurrentPage(1);
  };

  const handleFilter = async (displayMode = 'fade') => {
    // Ensure at least one variant is selected
    if (selectedVariants.length === 0) {
      alert('Please select at least one variant to filter by.');
      return;
    }

    // Get the selected variants
    const selectedVariantObjects = variants.filter(v => selectedVariants.includes(v.variant_id));
    if (selectedVariantObjects.length === 0) {
      alert('Selected variants not found.');
      return;
    }

    const seen = new Set();
    const variantActivitiesList = [];
    selectedVariantObjects.forEach((variant) => {
      const sources =
        Array.isArray(variant.exact_activities_list) && variant.exact_activities_list.length > 0
          ? variant.exact_activities_list
          : [variant.activities];
      sources.forEach((seq) => {
        const key = JSON.stringify(seq);
        if (!seen.has(key)) {
          seen.add(key);
          variantActivitiesList.push(seq);
        }
      });
    });

    // Set the variant filter in the global context
    dispatch({
      type: 'SET_VARIANT_FILTER',
      payload: {
        objectType: selectedObjectType,
        variantActivitiesList: variantActivitiesList,
        enabled: true,
        displayMode,
      }
    });

    console.log(
      `🔄 Variant filter applied: ${selectedVariantObjects.length} displayed variants ` +
      `(${variantActivitiesList.length} exact sequences) for ${selectedObjectType}`
    );
    selectedVariantObjects.forEach((variant, i) => {
      console.log(`   Variant ${i + 1} (${variant.variant_id}): ${variant.activities.join(' → ')}`);
    });
    console.log('🔄 Filter will be applied on next component refresh triggered by effect');
  };

  const handleReset = async () => {
    // Clear ONLY the variant filter from global context
    dispatch({
      type: 'SET_VARIANT_FILTER',
      payload: {
        objectType: null,
        variantActivitiesList: null,
        enabled: false
      }
    });

    setSelectedVariants(variants.map((v) => v.variant_id));
    setSortKey('trace_count');
    setSortOrder('desc');
    setSortOption('Most common variant');
    setCurrentPage(1);
    hasUserTouchedSelection.current = false;

    console.log('🔄 Variant filter cleared and checkboxes reset - other components will refresh with unfiltered data');
  };

  const sortedVariants = useMemo(() => {
    const sorted = [...variants].sort((a, b) => {
      let aVal = a[sortKey];
      let bVal = b[sortKey];

      if (sortKey === 'variant_id') {
        return sortOrder === 'asc'
          ? a.variant_id.localeCompare(b.variant_id)
          : b.variant_id.localeCompare(a.variant_id);
      }

      if (sortKey === 'abc_xyz_class') {
        // Composite class is a 2-char string (e.g. "AZ") or null. Sort it
        // alphabetically (A < B < C, then X < Y < Z), and always sink null /
        // absent classes to the bottom regardless of direction so the neutral
        // "—" rows don't interleave with classified variants.
        const aCls = a.abc_xyz_class;
        const bCls = b.abc_xyz_class;
        if (!aCls && !bCls) return 0;
        if (!aCls) return 1;
        if (!bCls) return -1;
        return sortOrder === 'asc'
          ? aCls.localeCompare(bCls)
          : bCls.localeCompare(aCls);
      }

      if (sortKey === 'median_lead_time') {
        aVal = a.lead_time_stats?.median ?? 0;
        bVal = b.lead_time_stats?.median ?? 0;
      }

      aVal = aVal ?? 0;
      bVal = bVal ?? 0;

      return sortOrder === 'asc' ? aVal - bVal : bVal - aVal;
    });

    return sorted;
  }, [variants, sortKey, sortOrder]);

  const indexOfLast = currentPage * VARIANTS_PER_PAGE;
  const currentVariants = sortedVariants.slice(indexOfLast - VARIANTS_PER_PAGE, indexOfLast);
  const totalPages = Math.ceil(variants.length / VARIANTS_PER_PAGE);

  const getPageGroup = () => {
    const groupStart = Math.floor((currentPage - 1) / 5) * 5 + 1;
    const groupEnd = Math.min(groupStart + 4, totalPages);
    return Array.from({ length: groupEnd - groupStart + 1 }, (_, i) => groupStart + i);
  };

  const handleBulkSelection = useCallback((variantIds) => {
    hasUserTouchedSelection.current = true;
    setSelectedVariants(variantIds);
  }, []);

  return {
    loading,
    isFiltered,
    isVariantFilterActive,
    error,
    hasLoaded,
    variants,
    summary,
    activityCatalog,
    selectedVariants,
    currentVariants,
    totalPages,
    currentPage,
    sortKey,
    sortOrder,
    sortOption,
    handleToggleSelect,
    toggleSelectAll,
    handleBulkSelection,
    handleSortChange,
    handleColumnSort,
    handleFilter,
    handleReset,
    setCurrentPage,
    getPageGroup,
    refetchData,
    dateRangeOverride,
    setDateRangeOverride,
  };
};
